import { useEffect, useRef, useState } from "react";

const WASM = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.1.0/wasm";
const MODEL = "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite";

// Oval guide in normalized container coords (matches PhotoCheck's 400x300 SVG).
export const OVAL = { cx: 0.5, cy: 140 / 300, rx: 78 / 400, ry: 104 / 300 };

export const FACE_HINTS = {
  loading: "Getting ready…",
  none: "We can't see your face — look at the camera",
  multiple: "Only one person should be in view",
  off_center: "Center your face inside the outline",
  too_small: "Move a little closer",
  too_big: "Move back a little",
  settling: "Hold still…",
  ok: "Perfect — hold still",
};

let detectorPromise = null;
const loadDetector = () => {
  if (!detectorPromise) {
    const timeout = new Promise((_, rej) => setTimeout(() => rej(new Error("detector timeout")), 12000));
    detectorPromise = Promise.race([timeout, (async () => {
      const { FaceDetector, FilesetResolver } = await import("@mediapipe/tasks-vision");
      const vision = await FilesetResolver.forVisionTasks(WASM);
      const opts = (delegate) => ({ baseOptions: { modelAssetPath: MODEL, delegate }, runningMode: "VIDEO", minDetectionConfidence: 0.6 });
      try { return await FaceDetector.createFromOptions(vision, opts("GPU")); }
      catch (_) { return await FaceDetector.createFromOptions(vision, opts("CPU")); }
    })()]);
    detectorPromise.catch(() => { detectorPromise = null; });
  }
  return detectorPromise;
};

// Map a detection box (video pixels) into the object-cover, mirrored 4:3 container.
function classify(dets, video) {
  if (!dets.length) return "none";
  if (dets.length > 1) return "multiple";
  const { originX, originY, width, height } = dets[0].boundingBox;
  const vw = video.videoWidth, vh = video.videoHeight;
  const cw = video.clientWidth, ch = video.clientHeight;
  const s = Math.max(cw / vw, ch / vh);
  const offX = (vw * s - cw) / 2, offY = (vh * s - ch) / 2;
  const fx = 1 - ((originX + width / 2) * s - offX) / cw;
  const fy = ((originY + height / 2) * s - offY) / ch;
  const fh = (height * s) / ch;
  const dx = (fx - OVAL.cx) / OVAL.rx, dy = (fy - OVAL.cy) / OVAL.ry;
  const ratio = fh / (OVAL.ry * 2);
  if (ratio > 0.95) return "too_big";
  if (dx * dx + dy * dy > 0.3) return "off_center";
  if (ratio < 0.42) return "too_small";
  return "ok";
}

// status: "loading" | "unavailable" | settling | none | multiple | off_center | too_small | too_big | ok
export function useFaceAlign(videoRef, active) {
  const [status, setStatus] = useState("loading");
  const okSince = useRef(0);
  useEffect(() => {
    if (!active) return undefined;
    let stop = false, timer = null, det = null;
    loadDetector().then(d => {
      det = d;
      const tick = () => {
        if (stop) return;
        const v = videoRef.current;
        if (v && v.readyState >= 2 && v.videoWidth) {
          try {
            const res = det.detectForVideo(v, performance.now());
            const st = classify(res.detections || [], v);
            // Require ~300ms of steady alignment before going green.
            if (st === "ok") { if (!okSince.current) okSince.current = performance.now(); }
            else okSince.current = 0;
            setStatus(st === "ok" && performance.now() - okSince.current < 300 ? "settling" : st);
          } catch (_) { /* skip frame */ }
        }
        timer = setTimeout(tick, 120);
      };
      tick();
    }).catch(() => { if (!stop) setStatus("unavailable"); });
    return () => { stop = true; clearTimeout(timer); };
  }, [active, videoRef]);
  return status;
}
